import { useMutation, useQueries } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { FiCheckCircle, FiEdit2, FiEye, FiPlus } from "react-icons/fi";
import { MdOutlineCancel } from "react-icons/md";
import { CustomTable, Modal } from "src/component";
import { CreateInventory } from "src/Modal";
import { format } from "date-fns";

import {
  deleteInventory,
  getAllBrands,
  getAllInventory,
  getAllLocations,
  updateInventory,
} from "src/services/api";
import { renderAlert, renderConfirmDialogue } from "src/utils/functions";
import { Trash2, EllipsisVertical } from "lucide-react";
import CustomDropdownMenu from "src/component/CustomDropdownMenu";
import CustomSelect from "src/component/CustomSelect";
import { useSearchParams } from "react-router-dom";
import { Button } from "src/components/ui/button";

const Inventory = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [modalOpen, setModalOpen] = useState(false);
  const [detailsModal, setDetailsModal] = useState(false);
  const [inventoryData, setInventoryData] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const page = parseInt(searchParams.get("page")) || 1;
  const perPage = parseInt(searchParams.get("per_page")) || 10;

  const [{ data, refetch }, { data: rawLocations }, { data: rawBrands }] =
    useQueries({
      queries: [
        {
          queryKey: [
            "inventory",
            selectedBrand,
            selectedLocation,
            perPage,
            parseInt(searchParams.get("page")),
          ],
          queryFn: () =>
            getAllInventory({
              location_id: selectedLocation,
              brand_id: selectedBrand,
              page,
              limit: perPage,
            }),
          suspense: true,
        },
        {
          queryKey: ["locations"],
          queryFn: getAllLocations,
          suspense: true,
        },
        {
          queryKey: ["brands"],
          queryFn: getAllBrands,
          suspense: true,
        },
      ],
    });

  const locations = rawLocations?.data;
  const brands = rawBrands?.data;

  useEffect(() => {
    if (!searchParams.has("page")) searchParams.set("page", 1);
    if (!searchParams.has("per_page")) searchParams.set("per_page", 10);
    setSearchParams(searchParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // useEffect(() => {
  //   console.log({ selectedLocation, selectedBrand });
  // }, [selectedLocation, selectedBrand]);

  const { mutate } = useMutation(updateInventory, {
    onSuccess: (res) => {
      renderAlert({
        title: "Success",
        msg: res?.msg,
        type: "success",
      });
      refetch();
    },
  });

  const { mutate: DeleteMutate } = useMutation(deleteInventory, {
    onSuccess: (res) => {
      renderAlert({
        title: "Success",
        msg: res?.msg,
        type: "success",
      });
      refetch();
    },
  });

  const tableActions = (row) => [
    {
      label: (
        <div className="flex items-center justify-start my-1 gap-[.5rem]  rounded-[1.2rem] text-[1.2rem] font-normal cursor-pointer hover:text-[#f40000]">
          {" "}
          <FiEdit2 size="15" />
          Update
        </div>
      ),
      onClick: () => {
        setModalOpen(true);
        setInventoryData(row);
      },
    },
    {
      label: (
        <div className="flex items-center justify-start my-1 gap-[.5rem]  rounded-[1.2rem] text-[1.2rem] font-normal cursor-pointer hover:text-[#f40000]">
          <FiEye size="16" />
          View Details
        </div>
      ),
      onClick: () => {
        setDetailsModal(true);
        setInventoryData(row);
      },
    },

    row?.status === "enabled"
      ? {
        label: (
          <div className="flex items-center justify-start my-1 gap-[.5rem]  rounded-[1.2rem] text-[1.2rem] font-normal cursor-pointer hover:text-[#f40000]">
            {" "}
            <MdOutlineCancel size="16" />
            Disable
          </div>
        ),
        onClick: () => {
          renderConfirmDialogue(
            "Are you sure?",
            "Are you sure you want to disable this inventory?",
            "Yes, disable it!"
          ).then((confirmed) => {
            if (confirmed) {
              mutate({
                id: row?.id,
                name: row?.name,
                quantity: row?.quantity,
                status: "Disabled",
              });
            }
          });
        },
      }
      : {
        label: (
          <div className="flex items-center justify-start my-1 gap-[.5rem]  rounded-[1.2rem] text-[1.2rem] font-normal cursor-pointer hover:text-[#f40000]">
            {" "}
            <FiCheckCircle size="16" />
            Enable
          </div>
        ),
        onClick: () => {
          renderConfirmDialogue(
            "Are you sure?",
            "Are you sure you want to enable this inventory?",
            "Yes, enable it!"
          ).then((confirmed) => {
            if (confirmed) {
              mutate({
                id: row?.id,
                name: row?.name,
                quantity: row?.quantity,
                status: "Enabled",
              });
            }
          });
        },
      },
    {
      label: (
        <div className="flex items-center justify-start my-1 gap-[.5rem]  rounded-[1.2rem] text-[1.2rem] font-normal cursor-pointer hover:text-[#f40000]">
          <Trash2 size="16" />
          Delete
        </div>
      ),
      onClick: () => {
        renderConfirmDialogue(
          "Are you sure?",
          "Are you sure you want to delete this inventory?",
          "Yes, delete it!"
        ).then((confirmed) => {
          if (confirmed) {
            DeleteMutate({ id: row?.id });
          }
        });
      },
    },
  ];

  const columns = [
    {
      name: "S/N",
      cell: (_, i) => i + 1,
      width: "60px",
    },
    {
      name: "Name",
      grow: 2,
      cell: (row) => <div className="capitalize">{row?.name}</div>,
    },
    {
      name: "Quantity",
      cell: (row) => row?.quantity,
    },
    {
      name: "Store",
      cell: (row) => <div className="capitalize">{row?.location}</div>,
    },
    {
      name: "Brand",
      cell: (row) => row?.brand,
    },
    {
      name: "Date",
      cell: (row) => {
        return row?.updated_at
          ? format(new Date(row?.updated_at), "dd/MM/yyyy")
          : "-";
      },
    },

    {
      name: "Status",
      cell: (row) => <div className="capitalize">{row?.status}</div>,
      width: "10%",
    },
    {
      name: "Actions",
      cell: (row) => (
        <CustomDropdownMenu
          trigger={<EllipsisVertical />}
          actions={tableActions(row)}
        />
      ),
    },
  ];
  return (
    <div>
      <div className="flex items-center justify-between mb-[3rem]">
        <p className="text-[2.5rem] font-medium text-defaultPrimary">
          Inventory
        </p>

        <div className="flex justify-center items-center gap-[3rem]">
          <button
            className="bg-lightPrimary py-[1rem] px-[1.8rem] text-defaultPrimary text-[1.5rem] rounded-lg flex items-center gap-2"
            onClick={() => {
              setModalOpen(true);
              setInventoryData("");
            }}
          >
            <FiPlus size={15} />
            Create Inventory
          </button>
        </div>
      </div>

      <div className="flex items-center gap-5">
        <CustomSelect
          key={
            selectedLocation === null
              ? "location-reset"
              : `location-${selectedLocation}`
          }
          containerClassName={"max-w-[350px]"}
          options={
            locations &&
            locations?.map(({ id, name }) => ({
              label: name,
              value: id,
            }))
          }
          placeholder="Select location"
          value={selectedLocation}
          onChange={(option) => setSelectedLocation(option)}
        />

        <CustomSelect
          key={
            selectedBrand === null ? "brand-reset" : `brand-${selectedBrand}`
          }
          containerClassName={"max-w-[350px]"}
          options={
            brands &&
            brands?.map(({ id, name }) => ({
              label: name,
              value: id,
            }))
          }
          placeholder="Select brand"
          value={selectedBrand}
          onChange={(option) => setSelectedBrand(option)}
        />
        <Button
          className={" py-[2.5rem] px-[1.8rem] text-xl"}
          onClick={() => {
            setSelectedBrand(null);
            setSelectedLocation(null);
          }}
        >
          Reset Filters
        </Button>
      </div>

      <div>
        <CustomTable
          columns={columns}
          data={data?.data?.records}
          paginationTotalRows={data?.data?.pagination?.total}
          paginationPerPage={perPage}
        />
      </div>
      <Modal
        isOpen={modalOpen}
        setIsOpen={setModalOpen}
        title={inventoryData ? "Update Inventory" : "Add Item To Inventory"}
      >
        <CreateInventory
          editData={inventoryData}
          refetch={refetch}
          setModalOpen={setModalOpen}
          rawLocations={rawLocations}
          rawBrands={rawBrands}
        />
      </Modal>
      <Modal
        isOpen={detailsModal}
        setIsOpen={setDetailsModal}
        title={"Inventory Details"}
        onClose={() => {
          setInventoryData("");
        }}
      >
        <InventoryDetails data={inventoryData} />
      </Modal>
    </div>
  );
};

const InventoryDetails = ({ data }) => {
  if (!data) return null;

  return (
    <div className="p-4">
      <ul className="space-y-2">
        <li>
          <strong>Name:</strong> {data?.name}
        </li>
        <li>
          <strong>Quantity:</strong> {data?.quantity}
        </li>
        <li>
          <strong>Cost Price:</strong> {data?.cost_price}
        </li>
        <li>
          <strong>Selling Price:</strong> {data?.selling_price}
        </li>
        <li className="capitalize">
          <strong>Store:</strong> {data?.location}
        </li>
        <li>
          <strong>Brand:</strong> {data?.brand}
        </li>
        <li className="capitalize">
          <strong>Status:</strong> {data?.status}
        </li>
        <li>
          <strong>Date:</strong>{" "}
          {format(new Date(data?.updated_at), "dd/MM/yyyy")}
        </li>
      </ul>
    </div>
  );
};

export default Inventory;
